import { useState, useEffect } from "react";
import { AdminLayout } from "./AdminDashboard";
import API from "../../lib/api";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Plus, Save, Trash2, Eye, EyeOff, Users } from "lucide-react";

export default function AdminWorkerTiers() {
  const [tiers, setTiers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", cost: 10000, base_rate: 20, food_cost: 10, starting_energy: 100, starting_efficiency: 1.0, color: "#94A3B8", lifespan_days: 0, lifespan_message: "" });
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState("");

  const load = async () => {
    try { const { data } = await API.get("/admin/worker-tiers"); setTiers(data.tiers); } catch {}
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setLoading("create");
    try {
      await API.post("/admin/worker-tiers", form);
      toast.success("Tier created");
      load();
      setShowCreate(false);
      setForm({ name: "", cost: 10000, base_rate: 20, food_cost: 10, starting_energy: 100, starting_efficiency: 1.0, color: "#94A3B8", lifespan_days: 0, lifespan_message: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(""); }
  };

  const save = async (tierId) => {
    setLoading(`save-${tierId}`);
    try {
      await API.put(`/admin/worker-tiers/${tierId}`, form);
      toast.success("Tier updated");
      load();
      setEditing(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(""); }
  };

  const deleteTier = async (tierId) => {
    if (!window.confirm("Delete this tier? Existing workers of this tier will keep working.")) return;
    try { await API.delete(`/admin/worker-tiers/${tierId}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  const TierForm = ({ onSave, saveLabel, saveLoading }) => (
    <div className="space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div><Label className="text-[#94A3B8] text-xs">Name</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="e.g. Bronze" className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-8 text-sm mt-1" data-testid="tier-name-input" /></div>
        <div><Label className="text-[#94A3B8] text-xs">Hire Cost (coins)</Label><Input type="number" value={form.cost} onChange={e => setForm({...form, cost: parseInt(e.target.value) || 0})} className="bg-[#0B0E14] border-[#2A3241] text-[#FFD600] h-8 text-sm mt-1 font-mono" /></div>
        <div><Label className="text-[#94A3B8] text-xs">Base Rate/Day</Label><Input type="number" value={form.base_rate} onChange={e => setForm({...form, base_rate: parseInt(e.target.value) || 0})} className="bg-[#0B0E14] border-[#2A3241] text-[#00F0FF] h-8 text-sm mt-1 font-mono" /></div>
        <div><Label className="text-[#94A3B8] text-xs">Food Cost/Day</Label><Input type="number" value={form.food_cost} onChange={e => setForm({...form, food_cost: parseInt(e.target.value) || 0})} className="bg-[#0B0E14] border-[#2A3241] text-[#EF4444] h-8 text-sm mt-1 font-mono" /></div>
        <div><Label className="text-[#94A3B8] text-xs">Starting Energy (max 100)</Label><Input type="number" min="1" max="100" value={form.starting_energy} onChange={e => setForm({...form, starting_energy: Math.min(parseInt(e.target.value) || 100, 100)})} className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-8 text-sm mt-1 font-mono" /></div>
        <div><Label className="text-[#94A3B8] text-xs">Starting Efficiency (max 1.0)</Label><Input type="number" step="0.1" min="0.1" max="1.0" value={form.starting_efficiency} onChange={e => setForm({...form, starting_efficiency: Math.min(parseFloat(e.target.value) || 1.0, 1.0)})} className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-8 text-sm mt-1 font-mono" /></div>
        <div><Label className="text-[#94A3B8] text-xs">Badge Color</Label><Input type="color" value={form.color} onChange={e => setForm({...form, color: e.target.value})} className="bg-[#0B0E14] border-[#2A3241] h-8 mt-1 w-full cursor-pointer" /></div>
        <div><Label className="text-[#94A3B8] text-xs">Lifespan (days, 0 = use global)</Label><Input type="number" min="0" value={form.lifespan_days} onChange={e => setForm({...form, lifespan_days: parseInt(e.target.value) || 0})} className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-8 text-sm mt-1 font-mono" /></div>
      </div>
      <div>
        <Label className="text-[#94A3B8] text-xs">Lifespan Message (empty = use global default)</Label>
        <Input value={form.lifespan_message} onChange={e => setForm({...form, lifespan_message: e.target.value})} placeholder="Leave empty to use global default message" className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-8 text-sm mt-1" />
      </div>
      <div className="flex items-center gap-3 text-xs text-[#64748B]">
        <span>Net/day: <strong className="text-[#10B981] font-mono">{form.base_rate - form.food_cost}c</strong></span>
        <span>ROI: <strong className="text-[#FFD600] font-mono">{form.base_rate - form.food_cost > 0 ? Math.round(form.cost / (form.base_rate - form.food_cost)) : "∞"} days</strong></span>
      </div>
      <button onClick={onSave} disabled={loading === saveLoading} data-testid="save-tier-btn"
        className="px-4 py-2 text-xs font-bold uppercase bg-[#10B981] text-white rounded-lg disabled:opacity-50">
        <Save className="w-3 h-3 inline mr-1" />{loading === saveLoading ? "Saving..." : saveLabel}
      </button>
    </div>
  );

  return (
    <AdminLayout title="Worker Tiers">
      <div className="space-y-4" data-testid="admin-worker-tiers">
        <div className="flex items-center justify-between">
          <p className="text-sm text-[#94A3B8]">Manage worker tiers. Users hire workers from the Shop.</p>
          <button onClick={() => setShowCreate(!showCreate)} data-testid="toggle-create-tier"
            className="flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-widest bg-[#00F0FF] text-[#0B0E14] rounded-lg hover:bg-[#00F0FF]/90">
            <Plus className="w-3 h-3" /> New Tier
          </button>
        </div>

        {showCreate && (
          <div className="bg-[#131722] rounded-xl border border-[#00F0FF]/30 p-4" data-testid="create-tier-form">
            <h3 className="text-sm font-bold text-[#F8FAFC] mb-3">Create Worker Tier</h3>
            <TierForm onSave={create} saveLabel="Create Tier" saveLoading="create" />
          </div>
        )}

        <div className="space-y-3">
          {tiers.map(t => (
            <div key={t.tier_id} className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-4" data-testid={`tier-${t.tier_id}`}>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${t.color}15`, border: `2px solid ${t.color}40` }}>
                  <Users className="w-5 h-5" style={{ color: t.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-[#F8FAFC]">{t.name}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-bold" style={{ backgroundColor: `${t.color}15`, color: t.color, border: `1px solid ${t.color}30` }}>{t.tier_id}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${t.enabled ? "bg-[#10B981]/10 text-[#10B981]" : "bg-[#EF4444]/10 text-[#EF4444]"}`}>{t.enabled ? "Active" : "Disabled"}</span>
                  </div>
                  <div className="flex items-center gap-4 mt-1 text-xs">
                    <span className="text-[#FFD600] font-mono">{t.cost}c cost</span>
                    <span className="text-[#00F0FF] font-mono">{t.base_rate}/day earn</span>
                    <span className="text-[#EF4444] font-mono">{t.food_cost}/day food</span>
                    <span className="text-[#10B981] font-mono">Net: {t.base_rate - t.food_cost}/day</span>
                    <span className="text-[#94A3B8] font-mono">ROI: {t.base_rate - t.food_cost > 0 ? Math.round(t.cost / (t.base_rate - t.food_cost)) : "∞"}d</span>
                    <span className="text-[#64748B] font-mono">Life: {t.lifespan_days > 0 ? `${t.lifespan_days}d` : "global"}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => { if (editing === t.tier_id) { setEditing(null); } else { setEditing(t.tier_id); setForm({ name: t.name, cost: t.cost, base_rate: t.base_rate, food_cost: t.food_cost, starting_energy: t.starting_energy, starting_efficiency: t.starting_efficiency, color: t.color, enabled: t.enabled, lifespan_days: t.lifespan_days || 0, lifespan_message: t.lifespan_message || "" }); }}}
                    className="px-3 py-1.5 text-xs font-bold uppercase text-[#00F0FF] border border-[#00F0FF]/30 rounded-lg hover:bg-[#00F0FF]/10">Edit</button>
                  <button onClick={() => deleteTier(t.tier_id)} className="p-1.5 text-[#EF4444] hover:bg-[#EF4444]/10 rounded"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              {editing === t.tier_id && (
                <div className="mt-4 pt-4 border-t border-[#2A3241]/30">
                  <TierForm onSave={() => save(t.tier_id)} saveLabel="Save Changes" saveLoading={`save-${t.tier_id}`} />
                  <button onClick={() => setForm({...form, enabled: !form.enabled})} className={`mt-2 px-3 py-1.5 text-xs font-bold uppercase rounded-lg border ${form.enabled ? "border-[#10B981]/30 text-[#10B981]" : "border-[#EF4444]/30 text-[#EF4444]"}`}>
                    {form.enabled ? <><Eye className="w-3 h-3 inline mr-1" />Enabled</> : <><EyeOff className="w-3 h-3 inline mr-1" />Disabled</>}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}
